import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

interface BlogPost {
    title: string;
    link: string;
    description: string;
    imageUrl: string;
    pubDate: string;
}

@customElement('blog-feed-component')
export class BlogFeedComponent extends LitElement {
    static amplitude_callsite: string = 'Blog Feed Component';

    @property({ type: String })
    rssUrl: string = 'https://www.proxi.co/blog/rss.xml';

    @property({ type: Number })
    maxPosts: number = 3;

    @property({ type: String })
    keywords: string = '';

    @property({ type: String })
    layout: 'grid' | 'sidebar' = 'grid';

    @state()
    private blogPosts: BlogPost[] = [];

    @state()
    private loading: boolean = true;

    @state()
    private error: string = '';

    @state()
    private dismissedPosts: Set<string> = new Set();

    @state()
    private isHidden: boolean = false;

   @state()
    private allFetchedPosts: BlogPost[] = [];

    private readonly DISMISSED_POSTS_KEY = 'blog-feed-dismissed-posts';
    private readonly COOLDOWN_KEY = 'blog-feed-cooldown-until';
    private readonly HIDDEN_KEY = 'blog-feed-hidden-until';
    private readonly COOLDOWN_DAYS = 3; // Number of days to hide after dismissing all posts
    private readonly HIDDEN_DAYS = 30; // Number of days to hide when user manually hides

    firstUpdated() {
        this.loadDismissedPosts();
        this.loadHiddenState();
        void this.fetchRSSFeed();
    }

    private loadDismissedPosts(): void {
        try {
            const stored = localStorage.getItem(this.DISMISSED_POSTS_KEY);
            if (stored) {
                const dismissedArray = JSON.parse(stored) as string[];
                this.dismissedPosts = new Set(dismissedArray);
            }
        } catch (error) {
            console.warn('Failed to load dismissed posts from localStorage:', error);
            this.dismissedPosts = new Set();
        }
    }

    private loadHiddenState(): void {
        try {
            const hiddenUntil = localStorage.getItem(this.HIDDEN_KEY);
            if (!hiddenUntil) {
                this.isHidden = false;
                return;
            }

            const hiddenDate = new Date(hiddenUntil);
            const now = new Date();
            this.isHidden = now < hiddenDate;

            // If the hidden period has expired, clean up localStorage
            if (!this.isHidden) {
                localStorage.removeItem(this.HIDDEN_KEY);
            }
        } catch (error) {
            console.warn('Failed to load hidden state from localStorage:', error);
            this.isHidden = false;
        }
    }

    private hideComponent(): void {
        try {
            const hiddenUntil = new Date();
            hiddenUntil.setDate(hiddenUntil.getDate() + this.HIDDEN_DAYS);
            localStorage.setItem(this.HIDDEN_KEY, hiddenUntil.toISOString());
            this.isHidden = true;
        } catch (error) {
            console.warn('Failed to save hidden state to localStorage:', error);
        }
    }

    private isInCooldownPeriod(): boolean {
        try {
            const cooldownUntil = localStorage.getItem(this.COOLDOWN_KEY);
            if (!cooldownUntil) return false;

            const cooldownDate = new Date(cooldownUntil);
            const now = new Date();
            return now < cooldownDate;
        } catch (error) {
            console.warn('Failed to check cooldown period:', error);
            return false;
        }
    }

    private startCooldownPeriod(): void {
        try {
            const cooldownUntil = new Date();
            cooldownUntil.setDate(cooldownUntil.getDate() + this.COOLDOWN_DAYS);
            localStorage.setItem(this.COOLDOWN_KEY, cooldownUntil.toISOString());
        } catch (error) {
            console.warn('Failed to start cooldown period:', error);
        }
    }

    private saveDismissedPosts(): void {
        try {
            const dismissedArray = Array.from(this.dismissedPosts);
            localStorage.setItem(this.DISMISSED_POSTS_KEY, JSON.stringify(dismissedArray));
        } catch (error) {
            console.warn('Failed to save dismissed posts to localStorage:', error);
        }
    }

    private dismissPost(postId: string): void {
        this.dismissedPosts = new Set([...this.dismissedPosts, postId]);
        this.saveDismissedPosts();
        // Re-filter and update displayed posts to show next available posts
        this.updateDisplayedPosts();
    }

    private updateDisplayedPosts(): void {
        // Filter by keywords if provided
        let filteredPosts = this.allFetchedPosts;
        if (this.keywords.trim() !== '') {
            const keywordList = this.keywords.toLowerCase().split(',').map(k => k.trim());
            filteredPosts = this.allFetchedPosts.filter(post => {
                const searchText = `${post.title} ${post.description}`.toLowerCase();
                return keywordList.some(keyword => searchText.includes(keyword));
            });
        }

        // Filter out dismissed posts BEFORE limiting to maxPosts
        const nonDismissedPosts = filteredPosts.filter(post => !this.dismissedPosts.has(post.link));

        // Check if user has dismissed all available posts
        if (filteredPosts.length > 0 && nonDismissedPosts.length === 0) {
            // User has dismissed all available posts - start cooldown period
            this.startCooldownPeriod();
            this.blogPosts = [];
            return;
        }

        // Limit to maxPosts from the non-dismissed posts
        this.blogPosts = nonDismissedPosts.slice(0, this.maxPosts);
    }

    private async fetchRSSFeed() {
    this.loading = true;
    this.error = '';

    try {
        // Use a CORS proxy to fetch the RSS feed
        const proxyUrl = 'https://api.allorigins.win/get?url=';
        const response = await fetch(proxyUrl + encodeURIComponent(this.rssUrl));

        if (!response.ok) {
            throw new Error('Failed to fetch RSS feed');
        }

        const data = await response.json();

        // Check if contents is a base64 data URL
        let xmlContent = data.contents as string;
        if (xmlContent.startsWith('data:application/rss+xml;base64,')) {
            // Extract and decode the base64 part
            const base64Data = xmlContent.split(',')[1];
            xmlContent = atob(base64Data);
        }

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

        const items = xmlDoc.querySelectorAll('item');
        const posts: BlogPost[] = [];

        items.forEach((item) => {
            const title = item.querySelector('title')?.textContent ?? '';
            const link = item.querySelector('link')?.textContent ?? '';
            const description = item.querySelector('description')?.textContent ?? '';
            const pubDate = item.querySelector('pubDate')?.textContent ?? '';

            // Extract image from media:content or media:thumbnail
            let imageUrl = '';
            const mediaContent = item.querySelector('content');
            const mediaThumbnail = item.querySelector('thumbnail');
            const enclosure = item.querySelector('enclosure');
            
            if (mediaContent) {
                imageUrl = mediaContent.getAttribute('url') ?? '';
            } else if (mediaThumbnail) {
                imageUrl = mediaThumbnail.getAttribute('url') ?? '';
            } else if (enclosure) {
                const type = enclosure.getAttribute('type') ?? '';
                if (type.startsWith('image/')) {
                    imageUrl = enclosure.getAttribute('url') ?? '';
                }
            } else {
                // Try to extract image from description HTML
                const descParser = new DOMParser();
                const descDoc = descParser.parseFromString(description, 'text/html');
                const img = descDoc.querySelector('img');
                if (img) {
                    imageUrl = img.getAttribute('src') ?? '';
                }
            }

            posts.push({
                title,
                link,
                description: this.stripHtml(description),
                imageUrl,
                pubDate,
            });
        });

        // Store all fetched posts for later re-filtering
        this.allFetchedPosts = posts;
        
        // Apply filtering and update displayed posts
        this.updateDisplayedPosts();
        this.loading = false;
    } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to load blog posts';
        this.loading = false;
    }
}

    private stripHtml(html: string): string {
        // Use DOMParser instead of innerHTML for security
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return doc.body.textContent ?? doc.body.innerText ?? '';
    }

    private formatDate(dateString: string): string {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } catch {
            return dateString;
        }
    }

    render() {
        // Check if user has manually hidden the component
        if (this.isHidden) {
            return html``;
        }

        // Check if in cooldown period (user dismissed all posts recently)
        if (this.isInCooldownPeriod()) {
            return html``;
        }

        // Only show content if we have successfully loaded blog posts
        if (this.loading || this.error || this.blogPosts.length === 0) {
            return html``;
        }

        return this.layout === 'sidebar' ? this.renderSidebar() : this.renderGrid();
    }

    private renderGrid() {
        return html`
            <div class="blog-feed-header">
                <h3 class="backpack__map-section-title">Latest Blog Posts</h3>
                <button class="hide-blog-feed-btn" @click="${this.hideComponent}" title="Hide blog posts">
                    ✕
                </button>
            </div>
            <div class="blog-feed">
                ${this.blogPosts.map(post => html`
                    <div class="blog-post-card" @click="${() => this.openPost(post.link)}">
                        ${post.imageUrl ? html`
                            <div class="blog-post-image">
                                <img src="${post.imageUrl}" alt="${post.title}" />
                            </div>
                        ` : ''}
                        <div class="blog-post-content">
                            <h3 class="blog-post-title">${post.title}</h3>
                            <p class="blog-post-description">${post.description}</p>
                            <span class="blog-post-read-more">Read more →</span>
                        </div>
                    </div>
                `)}
            </div>
        `;
    }

    private renderSidebar() {
        return html`
            <div class="blog-feed-sidebar">
                ${this.blogPosts.slice(0, 5).map(post => html`
                    <div class="sidebar-card">
                        <button class="dismiss-btn" @click="${(e: Event) => {
                            e.stopPropagation();
                            this.dismissPost(post.link);
                        }}" title="Dismiss this post" aria-label="Dismiss this post">
                            ✕
                        </button>
                        <div class="sidebar-card-content" @click="${() => this.openPost(post.link)}">
                            ${post.imageUrl ? html`
                                <div class="sidebar-card-image">
                                    <img src="${post.imageUrl}" alt="${post.title}" />
                                </div>
                            ` : ''}
                            <div class="sidebar-card-text">
                                <h4 class="sidebar-card-title">${post.title}</h4>
                            </div>
                        </div>
                    </div>
                `)}
                <span class="dismiss-all" @click="${this.hideComponent}"> Hide Blog Posts</span>
            </div>
        `;
    }

    private openPost(link: string) {
        window.open(link, '_blank');
    }

    static get styles() {
        return css`
            :host {
                display: block;
            }

            .loading-container,
            .error-container,
            .empty-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 2rem;
                text-align: center;
            }

            .dismiss-all {
                display: block;
                text-align: center;
                width: 100%;
                margin-top: 0.5rem;
                font-size: 0.6rem;
                color: var(--theme-text-color, #424242);
                cursor: pointer;
            }

            .error-container {
                color: var(--theme-error-color, #d32f2f);
            }

            .error-container mwc-icon {
                --mdc-icon-size: 48px;
                margin-bottom: 1rem;
            }

            .blog-feed {
                display: flex;
                flex-direction: row;
                gap: 2rem;
                padding: 1rem;
            }

            .blog-post-card {
                display: flex;
                background: var(--theme-white-color, #ffffff);
                border-radius: 8px;
                box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
                overflow: hidden;
                cursor: pointer;
                transition: transform 0.2s ease, box-shadow 0.2s ease;
            }

            .blog-post-card:hover {
                transform: translateY(-2px);
                box-shadow: rgba(0, 0, 0, 0.15) 0px 8px 12px -2px, rgba(0, 0, 0, 0.1) 0px 4px 8px -2px;
            }

            .blog-post-image {
                flex-shrink: 0;
                width: 200px;
                overflow: hidden;
                background-color: var(--theme-border-color, #e0e0e0);
            }

            .blog-post-image img {
                min-width: 100%;
                min-height: 100%;
                max-width: 100%;
                max-height: 100%;
                object-fit: cover;
            }

            .blog-post-content {
                padding: 1rem;
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }

            .blog-post-title {
                margin: 0;
                font-size: 0.8rem;
                font-weight: 700;
                color: var(--main-primary-color, #1976d2);
                line-height: 1.3;
            }

            .blog-post-date {
                margin: 0;
                font-size: 0.7rem;
                color: var(--theme-lighter-text-color, #757575);
            }

            .blog-post-description {
                margin: 0;
                font-size: 0.75rem;
                color: var(--theme-text-color, #424242);
                line-height: 1.5;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
            }

            .blog-post-read-more {
                margin-top: auto;
                font-size: 0.9rem;
                font-weight: 600;
                color: var(--main-primary-color, #1976d2);
            }

            /* Hide Button Styles */
            .blog-feed-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 1rem;
            }

            .hide-blog-feed-btn {
                background: rgba(0, 0, 0, 0.1);
                color: var(--theme-text-color, #424242);
                border: none;
                border-radius: 50%;
                width: 28px;
                height: 28px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                cursor: pointer;
                transition: background-color 0.2s ease, color 0.2s ease;
                line-height: 1;
                flex-shrink: 0;
            }

            .hide-blog-feed-btn:hover {
                background: rgba(0, 0, 0, 0.2);
                color: var(--theme-darker-text-color, #212121);
            }

            .hide-blog-feed-btn.sidebar-hide {
                position: absolute;
                top: 5px;
                right: 5px;
                z-index: 15;
                background: rgba(0, 0, 0, 0.6);
                color: white;
            }

            .hide-blog-feed-btn.sidebar-hide:hover {
                background: rgba(0, 0, 0, 0.8);
            }

            /* Sidebar Layout Styles */
            .blog-feed-sidebar {
                position: relative;
                padding: 40px 2rem 0.5rem;
                overflow: hidden;
                max-width: 300px;
                max-height: 170px;
                --fan-offset: 10px; /* Vertical spacing between fanned cards */
                --fan-width-reduction: 10%; /* How much each card shrinks */
                --opacity-step: 0.15; /* Opacity reduction per card */
                --card-height: 110px; /* Height of individual cards */
            }

            .sidebar-card {
                background: var(--theme-white-color, #ffffff);
                border-radius: 8px;
                box-shadow: rgba(0, 0, 0, 0.1) 0px 2px 4px -1px, rgba(0, 0, 0, 0.06) 0px 1px 2px -1px;
                overflow: hidden;
                transition: transform 0.2s ease, box-shadow 0.2s ease, z-index 0.1s ease, opacity 0.2s ease;
                animation: slideInFan 0.3s ease-out;
                height: var(--card-height, 110px);
            }

            /* First card - natural positioning */
            .sidebar-card:nth-child(1) {
                position: relative;
                width: 100%;
                z-index: 10;
                opacity: 1;
            }

            /* Subsequent cards - fan upward behind the first card */
            .sidebar-card:nth-child(n+2) {
                position: absolute;
                top: 0;
                left: 0;
            }

            .sidebar-card:nth-child(2) {
                top: calc(-1 * var(--fan-offset, 20px) + 35px);
                left: calc(3 * 15px);
                width: calc(70% - 1 * var(--fan-width-reduction, 5%));
                z-index: 9;
                opacity: calc(1 - 1 * var(--opacity-step, 0.15));
            }

            .sidebar-card:nth-child(3) {
                top: calc(-2 * var(--fan-offset, 20px) + 35px);
                left: calc(4 * 15px);
                width: calc(70% - 2 * var(--fan-width-reduction, 5%));
                z-index: 8;
                opacity: calc(1 - 2 * var(--opacity-step, 0.15));
            }

            .sidebar-card:nth-child(4) {
                top: calc(-3 * var(--fan-offset, 20px) + 35px);
                left: calc(5 * 15px);
                width: calc(70% - 3 * var(--fan-width-reduction, 5%));
                z-index: 7;
                opacity: calc(1 - 3 * var(--opacity-step, 0.15));
            }

            .sidebar-card:nth-child(5) {
                top: calc(-4 * var(--fan-offset, 20px) + 35px);
                left: calc(6 * 15px);
                width: calc(70% - 4 * var(--fan-width-reduction, 5%));
                z-index: 6;
                opacity: calc(1 - 4 * var(--opacity-step, 0.15));
            }

            .dismiss-btn {
                position: absolute;
                top: 8px;
                right: 8px;
                background: rgba(0, 0, 0, 0.6);
                color: white;
                border: 1px solid white;
                border-radius: 50%;
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
                cursor: pointer;
                z-index: 10;
                transition: background-color 0.2s ease;
                line-height: 1;
            }

            .dismiss-btn:hover {
                background: rgba(0, 0, 0, 0.8);
            }

            .sidebar-card-content {
                cursor: pointer;
                display: block;
            }

            .sidebar-card-image {
                width: 100%;
                height: 50px;
                overflow: hidden;
                background-color: var(--theme-border-color, #e0e0e0);
                position: relative;
            }

            .sidebar-card-image img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            .sidebar-card-text {
                padding: 0.5rem;
                height: 50px;
                overflow: hidden;
            }

            .sidebar-card-title {
                margin: 0 0 0.2rem 0;
                font-size: 0.7rem;
                font-weight: 600;
                color: var(--main-primary-color, #1976d2);
                line-height: 1.2;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
            }

            .sidebar-card-date {
                margin: 0 0 0.2rem 0;
                font-size: 0.55rem;
                color: var(--theme-lighter-text-color, #757575);
            }

            .sidebar-card-description {
                margin: 0;
                font-size: 0.6rem;
                color: var(--theme-text-color, #424242);
                line-height: 1.3;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
            }

            .backpack__map-section-title-wrapper {
                width: 100%;
                display: flex;
                justify-content: space-between;
                align-items: baseline;
                flex-wrap: wrap;
                gap: 5px 20px;
                margin: 10px 0 20px;
            }

            h3.backpack__map-section-title {
                color: #091E2C;
                text-align: left;
                font-size: 20px;
                font-weight: 700;
                line-height: 100%;
                margin: 0;
            }

            @keyframes slideInFan {
                from {
                    opacity: 0;
                    transform: translateX(-20px) scale(0.95);
                }
                to {
                    opacity: 1;
                    transform: translateX(0) scale(1);
                }
            }

            @media (max-width: 768px) {
                .blog-post-card {
                    flex-direction: column;
                }

                .blog-post-image {
                    width: 100%;
                    height: 200px;
                }

                .blog-feed-sidebar {
                    max-width: 100%;
                }
            }
        `;
    }
}
